import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as UploadICon } from 'src/Images/icons/common/upload.svg';
import { ReactComponent as DocumentIcon } from 'src/Images/icons/common/folder.svg';
import getColorCode from 'src/utils/GetThemeColor';
import 'src/style/invoice.css';

const compressImage = (
  file,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.95,
) =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

function DragDropArea({
  selectedFiles,
  isDragOver,
  setIsDragOver,
  themeColor,
  fileInputRef,
  onFilesAdded,
  disabled,
}) {
  const handleFileClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    if (disabled) return;
    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.capture = 'environment';
    cameraInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        onFilesAdded(files);
      }
    };
    cameraInput.click();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDragEnter = () => {
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      onFilesAdded(imageFiles);
    }
  };

  const formatFileSize = (size) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / 1048576).toFixed(2)} MB`;
  };

  const hasFiles = selectedFiles.length > 0;

  return (
    <div
      className={`file-upload-container div-flex-column rg-10 div-align-center ${isDragOver ? 'drag-over' : ''}`}
      onClick={handleFileClick}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabIndex="0"
    >
      {hasFiles ? (
        <DocumentIcon className="upload-icon-size-sm" />
      ) : (
        <UploadICon
          className="upload-icon-size"
          color={getColorCode(themeColor)}
        />
      )}
      <div className="div-flex-column rg-5 div-align-center">
        {hasFiles ? (
          <>
            <span className="text-lg text-semi-bold">
              {selectedFiles.length}{' '}
              {selectedFiles.length === 1 ? 'file' : 'files'} selected
              <span className="text-grey">
                {' '}
                (
                {formatFileSize(
                  selectedFiles.reduce((sum, f) => sum + f.size, 0),
                )}
                )
              </span>
            </span>
            <div className="div-flex-row cg-10 margin-top-10">
              <button
                className="vehicle-tracking-detail-add-vehcile-data-btn theme-border-color-grey div-align-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCameraClick();
                }}
                disabled={disabled}
              >
                <span className="text-md">Take Photo</span>
              </button>
              <button
                className={`vehicle-tracking-detail-move-stage-btn theme-background-${themeColor} div-align-center`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileClick();
                }}
                disabled={disabled}
              >
                <span className="text-md">Browse files</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="text-lg text-semi-bold">Drag and Drop</span>
            <span className="text-sm text-grey">
              your file here or&nbsp;
              <span className={`text-color-${themeColor}`}>Browse</span>
            </span>
            <span
              className="text-sm"
              style={{ color: getColorCode(themeColor) }}
            >
              Only image files with max size 50MB. Photos over 2MB are
              automatically compressed.
            </span>

            <div className="upload-divider-container">
              <div className="upload-divider-line" />
              <span className="text-sm text-grey upload-divider-text">Or</span>
              <div className="upload-divider-line" />
            </div>

            <button
              className={`take-photo-btn theme-background-${themeColor} div-align-center`}
              onClick={(e) => {
                e.stopPropagation();
                handleCameraClick();
              }}
              disabled={disabled}
            >
              <span className="text-md">Take Photo</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function UploadPODComponent({
  onFileUpload,
  disabled = false,
  uploading = false,
  onFilesUploaded,
  hideUploadButton = false,
  themeColor = 'blueTheme',
}) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const hasFiles = uploadedFiles.length > 0;

  useEffect(() => {
    if (onFilesUploaded) {
      const files = uploadedFiles.map((entry) => entry.file);
      onFilesUploaded(files);
    }
  }, [uploadedFiles, onFilesUploaded]);

  useEffect(
    () => () => {
      uploadedFiles.forEach((entry) => {
        if (entry.previewUrl) {
          URL.revokeObjectURL(entry.previewUrl);
        }
      });
    },
    [uploadedFiles],
  );

  const addFiles = async (files) => {
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith('image/')) return false;
      if (file.size > 10 * 1024 * 1024) return false;
      return true;
    });

    if (validFiles.length === 0) return;

    const processedFiles = await Promise.all(
      validFiles.map(async (file) => {
        if (file.size > 2 * 1024 * 1024) {
          return compressImage(file);
        }
        return file;
      }),
    );

    const newEntries = processedFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setUploadedFiles((prev) => [...prev, ...newEntries]);
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      await addFiles(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (previewUrl) => {
    setUploadedFiles((prev) => {
      const removed = prev.find((p) => p.previewUrl === previewUrl);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return prev.filter((p) => p.previewUrl !== previewUrl);
    });
  };

  const handleUpload = () => {
    if (uploadedFiles.length > 0) {
      const files = uploadedFiles.map((entry) => entry.file);
      onFileUpload(files);
    }
  };

  const clearAll = () => {
    uploadedFiles.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    setUploadedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderThumbnails = () => {
    if (!hasFiles) return null;
    return (
      <div className="invoice-thumbnails-container">
        {uploadedFiles.map((entry) => (
          <div key={entry.previewUrl} className="invoice-thumbnail-item">
            <img
              src={entry.previewUrl}
              alt="POD preview"
              className="invoice-thumbnail-image"
            />
            <button
              className="invoice-thumbnail-remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                removeFile(entry.previewUrl);
              }}
              title="Remove"
              aria-label="Remove"
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="invoice-capture-container">
      <div className="div-flex-column rg-15">
        <div className="file-upload-tab">
          <DragDropArea
            selectedFiles={uploadedFiles.map((e) => e.file)}
            isDragOver={isDragOver}
            setIsDragOver={setIsDragOver}
            themeColor={themeColor}
            fileInputRef={fileInputRef}
            onFilesAdded={addFiles}
            disabled={disabled || uploading}
          />

          {renderThumbnails()}

          {hasFiles && !hideUploadButton && (
            <div className="upload-actions-bar">
              <button
                className="vehicle-tracking-detail-add-vehcile-data-btn theme-border-color-grey div-align-center"
                onClick={clearAll}
              >
                <span className="text-md">Clear</span>
              </button>
              <button
                className={`vehicle-tracking-detail-move-stage-btn theme-background-${themeColor} div-align-center`}
                onClick={handleUpload}
                disabled={uploading}
              >
                <span className="text-md">
                  {uploading
                    ? 'Uploading...'
                    : `Upload ${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'File' : 'Files'}`}
                </span>
              </button>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
}
